import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { KeyOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Modal,
  notification,
  Spin,
  Typography,
} from "antd";
import { observer } from "mobx-react-lite";

import {
  type ExternalServiceKey,
  type ExternalServiceType,
  getExternalServiceKeys,
  issueExternalServiceKey,
} from "../api/externalServices";
import { CredentialField } from "../components/util/CredentialField";
import { userStore } from "../stores/userStore";
import { sendCounterEvent } from "../utils/counterService";
import { usePendingAction } from "../utils/usePendingAction";

import "./ExternalServicesPage.css";

interface ExternalServicesPageProps {
  services: ExternalServiceType[];
  isActive: boolean;
}

type KeysLoadError = "load" | "refresh";

const MASK = "•".repeat(8);

const maskServiceUrl = (url: string) => {
  try {
    return `${new URL(url).origin}/${MASK}`;
  } catch {
    return MASK;
  }
};

export const ExternalServicesPage = observer(function ExternalServicesPage({
  services,
  isActive,
}: ExternalServicesPageProps) {
  const { t, i18n } = useTranslation();
  const [keys, setKeys] = useState<ExternalServiceKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState<ExternalServiceType | null>(null);
  const [loadError, setLoadError] = useState<KeysLoadError | null>(null);
  const { run: runIssueKey } = usePendingAction();

  const osminfoDocsUrl = `https://docs.nextgis.${
    userStore.isRuRegion ? "ru" : "com"
  }/docs_ngqgis/source/osminfo.html#osminfo-overpass`;

  const loadKeys = useCallback(async (errorType: KeysLoadError = "load") => {
    try {
      setKeys(await getExternalServiceKeys());
      setLoadError(null);
      return true;
    } catch {
      setKeys([]);
      setLoadError(errorType);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadKeys();
  }, [loadKeys]);

  const reloadKeys = async () => {
    await runIssueKey(async () => {
      setLoading(true);
      await loadKeys(loadError ?? "load");
    });
  };

  const issueKey = async (serviceType: ExternalServiceType) => {
    await runIssueKey(async () => {
      setIssuing(serviceType);
      try {
        await issueExternalServiceKey(serviceType);
        setKeys([]);
        setLoading(true);
        if (await loadKeys("refresh")) {
          notification.success({ message: t("externalServices.keyIssued") });
        }
      } catch {
        notification.error({ message: t("errors.shared.common_error") });
      } finally {
        setIssuing(null);
      }
    });
  };

  const requestKey = (serviceType: ExternalServiceType, hasKey: boolean) => {
    if (!hasKey) {
      void issueKey(serviceType);
      return;
    }
    Modal.confirm({
      title: t("externalServices.rotateTitle"),
      content: t("externalServices.rotateWarning"),
      okText: t("externalServices.rotate"),
      cancelText: t("app.cancel"),
      okButtonProps: { danger: true },
      onOk: () => issueKey(serviceType),
    });
  };

  return (
    <div className="external-services-page">
      {loading ? (
        <div className="external-services-page__loading">
          <Spin size="large" />
        </div>
      ) : loadError ? (
        <Alert
          type="error"
          showIcon
          description={t(
            loadError === "refresh"
              ? "externalServices.refreshError"
              : "externalServices.loadError",
          )}
          action={
            <Button onClick={() => void reloadKeys()}>
              {t("externalServices.reload")}
            </Button>
          }
        />
      ) : (
        <>
          <div className="external-services-page__grid">
            {services.map((serviceType) => {
              const item = keys.find((key) => key.service_type === serviceType);
              return (
                <Card
                  key={serviceType}
                  title={t(`externalServices.services.${serviceType}.title`)}
                >
                  <Typography.Paragraph>
                    <Trans
                      i18nKey={`externalServices.services.${serviceType}.description`}
                      components={{
                        lnk: (
                          <a
                            href={osminfoDocsUrl}
                            target="_blank"
                            rel="noreferrer"
                          />
                        ),
                      }}
                    />
                  </Typography.Paragraph>
                  {item && isActive && (
                    <div
                      key={item.key}
                      className="external-services-page__credentials"
                    >
                      <CredentialField
                        label={t("externalServices.apiKey")}
                        value={item.key}
                        maskedValue={MASK}
                        showLabel={t("externalServices.showKey")}
                        hideLabel={t("externalServices.hideKey")}
                        copyLabel={t("externalServices.copyKey")}
                      />
                      <CredentialField
                        label={t("externalServices.serviceUrl")}
                        value={item.url}
                        maskedValue={maskServiceUrl(item.url)}
                        showLabel={t("externalServices.showUrl")}
                        hideLabel={t("externalServices.hideUrl")}
                        copyLabel={t("externalServices.copyUrl")}
                      />
                      <Typography.Text type="secondary">
                        {item.last_used_at
                          ? t("externalServices.lastUsed", {
                              date: new Date(item.last_used_at).toLocaleString(
                                i18n.language,
                              ),
                            })
                          : t("externalServices.neverUsed")}
                      </Typography.Text>
                    </div>
                  )}
                  <Button
                    type={item ? "default" : "primary"}
                    icon={<KeyOutlined />}
                    loading={issuing === serviceType}
                    disabled={issuing !== null && issuing !== serviceType}
                    onClick={() => requestKey(serviceType, !!item)}
                  >
                    {t(
                      item
                        ? "externalServices.rotate"
                        : "externalServices.issue",
                    )}
                  </Button>
                </Card>
              );
            })}
          </div>
          <Alert
            type="info"
            showIcon
            className="external-services-page__help"
            description={
              <Trans
                i18nKey="externalServices.help"
                components={{
                  mini: (
                    <Link
                      to="/subscription/update/manage?plan=mandarin&period=monthly"
                      onClick={() => sendCounterEvent("help_mini")}
                    />
                  ),
                  premium: (
                    <Link
                      to="/subscription/update/manage?plan=pomelo&period=monthly"
                      onClick={() => sendCounterEvent("help_premium")}
                    />
                  ),
                }}
              />
            }
          />
        </>
      )}
    </div>
  );
});
